// /parent/health-records — calm, mobile-first.
// Per child: action-needed items at top, then upcoming/current/withdrawn.
// Parents can upload a record (issued/expires + optional file). Admin
// retains the right to revise or revoke.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Warning, FileText, Plus, X, FileArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { listChildrenForParent } from "@/lib/dailyReports";
import {
  STATUS_META,
  daysUntilExpiry,
  deriveStatus,
  getChildHealthDocumentUrl,
  listChildHealthRecordTypes,
  listChildHealthRecordsForChildren,
  rollupByChild,
  suggestExpiryDate,
  typeAppliesToChild,
  upsertChildHealthRecord,
  uploadChildHealthDocument,
} from "@/lib/childHealth";
import {
  activeApprovedExemption,
  deriveExemptionStatus,
  EXEMPTION_STATUS_META,
  getHealthExemptionDocumentUrl,
  listHealthExemptionsForChildren,
  listMedicalProfilesForChildren,
} from "@/lib/healthOperations";
import MedicalProfileSheet from "@/components/health/MedicalProfileSheet";
import HealthExemptionSheet from "@/components/health/HealthExemptionSheet";

export default function ParentHealthRecords() {
  const { profile } = useAuth();
  const profileId = profile?.id;
  const organizationId = profile?.organization_id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [types, setTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, child: null, record: null, presetTypeId: null });
  const [profileEditor, setProfileEditor] = useState({ open: false, child: null });
  const [exemptionEditor, setExemptionEditor] = useState({ open: false, child: null, presetTypeId: null });

  const refresh = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const kids = await listChildrenForParent(profileId);
      setChildren(kids || []);
      const orgId = kids?.[0]?.organization_id || organizationId;
      const [t, r, p, e] = await Promise.all([
        listChildHealthRecordTypes(orgId),
        listChildHealthRecordsForChildren(kids.map((c) => c.id)),
        listMedicalProfilesForChildren(kids.map((c) => c.id)),
        listHealthExemptionsForChildren(kids.map((c) => c.id)),
      ]);
      setTypes(t || []);
      setRecords(r || []);
      setProfiles(p || []);
      setExemptions(e || []);
    } catch (err) {
      toast.error(err.message || "Could not load health records");
    } finally {
      setLoading(false);
    }
  }, [organizationId, profileId]);
  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link: /parent/health-records?childId=…&openType=… opens the
  // editor preset to that type once data has loaded. Used by the
  // ClearanceChip "Fix" CTA to skip a click.
  useEffect(() => {
    if (loading) return;
    const childId = searchParams.get("childId");
    const openType = searchParams.get("openType");
    if (!childId) return;
    const child = children.find((c) => c.id === childId);
    if (!child) return;
    setEditor({ open: true, child, record: null, presetTypeId: openType || null });
    // Strip the params so a refresh doesn't re-open the sheet.
    const next = new URLSearchParams(searchParams);
    next.delete("childId");
    next.delete("openType");
    setSearchParams(next, { replace: true });
  }, [loading, children, searchParams, setSearchParams]);

  const rollups = useMemo(
    () => rollupByChild(records, types, children, exemptions),
    [records, types, children, exemptions],
  );
  const overallActionCount = rollups.reduce((n, r) => n + r.counts.expired + r.counts.expiring_soon + r.counts.missing
    + r.exemptions.filter((e) => deriveExemptionStatus(e) === "expiring_soon").length, 0);
  const profilesByChild = useMemo(
    () => new Map(profiles.map((profile) => [profile.child_id, profile])),
    [profiles],
  );

  const onAdd = (child, presetTypeId = null) => setEditor({ open: true, child, record: null, presetTypeId });
  const onEdit = (child, record) => setEditor({ open: true, child, record, presetTypeId: null });

  return (
    <div data-testid="parent-health-records" className="space-y-5">
      <div>
        <Link
          to="/parent"
          data-testid="parent-health-records-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Health records
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          Keep your child's required health and immunization records up to date.
          Your center will send reminders before anything expires.
        </p>
      </div>

      <section className="grid grid-cols-2 gap-3">
        <Stat testId="parent-health-stat-children" label="Children" value={children.length} />
        <Stat
          testId="parent-health-stat-action"
          label="Need attention"
          value={overallActionCount}
          tone={overallActionCount > 0 ? "warn" : "ok"}
        />
      </section>

      {loading && <p className="text-sm text-neutral-500">Loading…</p>}

      {!loading && children.length === 0 && (
        <p
          data-testid="parent-health-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500"
        >
          No children linked to your family yet.
        </p>
      )}

      <ul className="space-y-5" data-testid="parent-health-children">
        {rollups.map((r) => (
          <li key={r.child.id} data-testid={`parent-health-child-${r.child.id}`} data-worst={r.worst}>
            <ChildSection
              rollup={r}
              types={types}
              profile={profilesByChild.get(r.child.id) || null}
              onAdd={(presetTypeId) => onAdd(r.child, presetTypeId)}
              onEdit={(rec) => onEdit(r.child, rec)}
              onOpenProfile={() => setProfileEditor({ open: true, child: r.child })}
              onRequestExemption={(presetTypeId) => setExemptionEditor({ open: true, child: r.child, presetTypeId })}
            />
          </li>
        ))}
      </ul>

      <RecordEditorSheet
        open={editor.open}
        onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))}
        child={editor.child}
        record={editor.record}
        presetTypeId={editor.presetTypeId}
        types={types}
        onSaved={refresh}
      />
      <MedicalProfileSheet
        open={profileEditor.open}
        onOpenChange={(open) => setProfileEditor((state) => ({ ...state, open }))}
        child={profileEditor.child}
        profile={profilesByChild.get(profileEditor.child?.id) || null}
        onSaved={refresh}
      />
      <HealthExemptionSheet
        open={exemptionEditor.open}
        onOpenChange={(open) => setExemptionEditor((state) => ({ ...state, open }))}
        child={exemptionEditor.child}
        types={types}
        presetTypeId={exemptionEditor.presetTypeId}
        onSaved={refresh}
      />
    </div>
  );
}

function Stat({ testId, label, value, tone }) {
  const cls = { ok: "border-emerald-200", warn: "border-amber-300" }[tone || ""] || "border-neutral-200";
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-4 ${cls}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function ChildSection({ rollup, types, profile, onAdd, onEdit, onOpenProfile, onRequestExemption }) {
  const { child, records, missing, counts, configuration, exemptions } = rollup;
  const overdue = records.filter((r) => r.status === "expired");
  const expiring = records.filter((r) => r.status === "expiring_soon");
  const current = records.filter((r) => r.status === "valid");
  const withdrawn = records.filter((r) => r.status === "revoked");

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            {child.first_name} {child.last_name || ""}
          </p>
          <p className="text-[11px] text-neutral-500">
            {counts.expired + counts.missing > 0
              ? `${counts.expired + counts.missing} need${counts.expired + counts.missing === 1 ? "s" : ""} attention`
              : counts.expiring_soon > 0
                ? `${counts.expiring_soon} expiring soon`
                : "All current"}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          <button
            type="button"
            data-testid={`parent-health-profile-${child.id}`}
            onClick={onOpenProfile}
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            {profile ? "Care details" : "Add care details"}
          </button>
          <button
            type="button"
            data-testid={`parent-health-add-${child.id}`}
            onClick={() => onAdd(null)}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-primary-hover"
          >
            <Plus size={11} weight="bold" /> Add record
          </button>
        </div>
      </div>

      {configuration.length > 0 && (
        <Group label="Center configuration" tone="muted" testId={`parent-health-group-configuration-${child.id}`}>
          <p className="rounded-2xl border border-neutral-200 bg-neutral-50/60 px-3 py-3 text-[13px] text-neutral-600">
            Your center is finishing how {configuration.map((type) => type.label).join(", ")} applies to this classroom. No family action is needed right now.
          </p>
        </Group>
      )}

      {missing.length > 0 && (
        <Group label="Missing — please upload" tone="alert" testId={`parent-health-group-missing-${child.id}`}>
          <ul className="space-y-2">
            {missing.map((t) => (
              <MissingRow key={t.id} child={child} type={t} onAdd={() => onAdd(t.id)} onRequestExemption={() => onRequestExemption(t.id)} />
            ))}
          </ul>
        </Group>
      )}
      {overdue.length > 0 && (
        <Group label="Overdue" tone="alert" testId={`parent-health-group-overdue-${child.id}`}>
          <RecordList rows={overdue} onEdit={onEdit} testIdPrefix={`parent-health-record`} />
        </Group>
      )}
      {expiring.length > 0 && (
        <Group label="Expiring soon" tone="warn" testId={`parent-health-group-expiring-${child.id}`}>
          <RecordList rows={expiring} onEdit={onEdit} testIdPrefix={`parent-health-record`} />
        </Group>
      )}
      {current.length > 0 && (
        <Group label="Current" tone="ok" testId={`parent-health-group-current-${child.id}`}>
          <RecordList rows={current} onEdit={onEdit} testIdPrefix={`parent-health-record`} />
        </Group>
      )}
      {withdrawn.length > 0 && (
        <Group label="Withdrawn" tone="muted" testId={`parent-health-group-withdrawn-${child.id}`}>
          <RecordList rows={withdrawn} onEdit={onEdit} testIdPrefix={`parent-health-record`} />
        </Group>
      )}
      {exemptions.length > 0 && (
        <Group label="Exemption requests" tone="muted" testId={`parent-health-group-exemptions-${child.id}`}>
          <ExemptionList rows={exemptions} />
        </Group>
      )}

      {records.length === 0 && missing.length === 0 && (
        <p className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-3 text-center text-[13px] text-neutral-500">
          No records on file yet.
        </p>
      )}
    </div>
  );
}

function Group({ label, tone, testId, children }) {
  return (
    <section data-testid={testId} className="mt-3">
      <p className={`mb-1.5 text-[11px] font-bold uppercase tracking-wider ${
        tone === "alert" ? "text-[#8A5A12]"
        : tone === "warn" ? "text-amber-800"
        : tone === "ok" ? "text-emerald-700"
        : "text-neutral-500"
      }`}>{label}</p>
      {children}
    </section>
  );
}

function MissingRow({ child, type, onAdd, onRequestExemption }) {
  return (
    <li
      data-testid={`parent-health-missing-${child.id}-${type.id}`}
      className="flex items-center gap-3 rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-3"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#8A5A12]">
        <Warning size={16} weight="duotone" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-neutral-900">{type.label}</p>
        <p className="text-[11px] text-neutral-500">No record on file</p>
      </div>
      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
        <button
          type="button"
          data-testid={`parent-health-missing-exemption-${child.id}-${type.id}`}
          onClick={onRequestExemption}
          className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          Request review
        </button>
        <button
          type="button"
          data-testid={`parent-health-missing-add-${child.id}-${type.id}`}
          onClick={onAdd}
          className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover"
        >
          Upload
        </button>
      </div>
    </li>
  );
}

function ExemptionList({ rows }) {
  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const status = deriveExemptionStatus(row);
        const meta = EXEMPTION_STATUS_META[status] || EXEMPTION_STATUS_META.submitted;
        const current = activeApprovedExemption([row], row.record_type_id);
        const onOpenDocument = async () => {
          const url = await getHealthExemptionDocumentUrl(row.document_path);
          if (url) window.open(url, "_blank", "noopener");
          else toast.error("Document not available");
        };
        return (
          <li key={row.id} data-testid={`parent-health-exemption-${row.id}`} data-status={status} className="rounded-2xl border border-neutral-200 bg-white p-3">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover"><ShieldCheck size={16} weight="duotone" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-neutral-900">{row.record_type?.label || "Health requirement"}</p>
                <p className="text-[11px] text-neutral-500">
                  Submitted {String(row.submitted_at || "").slice(0, 10) || "recently"}
                  {row.expires_on ? ` · expires ${row.expires_on}` : ""}
                  {current ? " · currently satisfies this requirement" : ""}
                </p>
                {row.review_note && <p className="mt-1 text-[11px] text-neutral-600">Center note: {row.review_note}</p>}
              </div>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>{meta.label}</span>
            </div>
            {row.document_path && (
              <div className="mt-2 flex justify-end">
                <button type="button" onClick={onOpenDocument} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
                  <FileText size={11} weight="duotone" /> Open supporting document
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function RecordList({ rows, onEdit, testIdPrefix }) {
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <RecordRow key={r.id} record={r} onEdit={() => onEdit(r)} testId={`${testIdPrefix}-${r.id}`} />
      ))}
    </ul>
  );
}

function RecordRow({ record, onEdit, testId }) {
  const status = deriveStatus(record);
  const meta = STATUS_META[status];
  const days = daysUntilExpiry(record.expires_on);
  const onOpenDoc = async () => {
    const url = await getChildHealthDocumentUrl(record.document_path);
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("Document not available");
  };
  return (
    <li
      data-testid={testId}
      data-status={status}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
          <ShieldCheck size={16} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">{record.record_type?.label}</p>
          <p className="text-[11px] text-neutral-500">
            Issued {record.issued_on}
            {record.expires_on
              ? ` · expires ${record.expires_on}${days !== null ? ` (${days >= 0 ? `in ${days}d` : `${Math.abs(days)}d ago`})` : ""}`
              : " · no expiry"}
          </p>
          {record.notes && <p className="mt-0.5 text-[11px] italic text-neutral-500">"{record.notes}"</p>}
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
          {meta.label}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 justify-end">
        {record.document_path && (
          <button type="button" data-testid={`${testId}-doc`} onClick={onOpenDoc}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
            <FileText size={11} weight="duotone" /> Open
          </button>
        )}
        {!record.revoked_at && (
          <button type="button" data-testid={`${testId}-edit`} onClick={onEdit}
            className="rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
            Update
          </button>
        )}
      </div>
    </li>
  );
}

function RecordEditorSheet({ open, onOpenChange, child, record, presetTypeId, types, onSaved }) {
  const isEdit = !!record;
  const [typeId, setTypeId] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  // Filter types by child's age window so parents don't see options that
  // don't apply (e.g. MMR for a 4-month-old).
  const applicableTypes = useMemo(
    () => (child ? types.filter((t) => typeAppliesToChild(t, child)) : []),
    [types, child],
  );

  useEffect(() => {
    if (!open) return;
    setTypeId(record?.record_type_id || presetTypeId || applicableTypes[0]?.id || "");
    setIssuedOn(record?.issued_on || new Date().toISOString().slice(0,10));
    setExpiresOn(record?.expires_on || "");
    setNotes(record?.notes || "");
    setFile(null);
  }, [open, record, presetTypeId, applicableTypes]);

  // Auto-suggest expires_on from default validity (only on new + when blank).
  useEffect(() => {
    if (!open || record) return;
    const t = applicableTypes.find((x) => x.id === typeId);
    if (t?.default_validity_months && issuedOn && !expiresOn) {
      setExpiresOn(suggestExpiryDate(issuedOn, t.default_validity_months));
    }
  }, [typeId, issuedOn, open, record, applicableTypes, expiresOn]);

  const onSave = async () => {
    if (!child?.id) return;
    if (!typeId) { toast.error("Pick a record type"); return; }
    if (!issuedOn) { toast.error("Pick an issue date"); return; }
    setBusy(true);
    try {
      const saved = await upsertChildHealthRecord({
        id: record?.id,
        childId: child.id,
        recordTypeId: typeId,
        issuedOn,
        expiresOn: expiresOn || null,
        documentPath: record?.document_path || null,
        notes,
      });
      if (file && saved?.id) {
        await uploadChildHealthDocument({ recordId: saved.id, file });
      }
      toast.success(isEdit ? "Record updated" : "Record uploaded");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="parent-health-editor"
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto rounded-t-3xl sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{isEdit ? "Update record" : "Upload record"}</SheetTitle>
          <SheetDescription>
            For <strong>{child?.first_name}</strong>. Reach out to your center if you need help.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <Field label="Type">
            <select
              data-testid="parent-health-editor-type"
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              className={INPUT}
              disabled={isEdit}
            >
              {applicableTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}{t.is_required ? " · required" : ""}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issued on">
              <input type="date" data-testid="parent-health-editor-issued" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} className={INPUT} />
            </Field>
            <Field label="Expires on (optional)">
              <input type="date" data-testid="parent-health-editor-expires" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} className={INPUT} />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea rows={2} data-testid="parent-health-editor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Attach proof (optional, PDF / image)">
            <input
              type="file"
              accept="application/pdf,image/*"
              data-testid="parent-health-editor-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-neutral-700"
            />
            {record?.document_path && !file && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-neutral-500">
                <FileArrowUp size={11} weight="duotone" />
                Existing doc on file. Picking a new file replaces it after save.
              </p>
            )}
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button type="button" data-testid="parent-health-editor-cancel" onClick={() => onOpenChange?.(false)} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <X size={12} weight="bold" /> Cancel
          </button>
          <button type="button" data-testid="parent-health-editor-save" disabled={busy} onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60">
            <ShieldCheck size={12} weight="duotone" />
            {busy ? "Saving…" : isEdit ? "Save changes" : "Upload record"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
